import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, Zap, ArrowRight, ShieldCheck, MessageCircle } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { track } from '@/lib/tracking';
import { useOverlayLock } from '@/lib/overlay';
import {
  money,
  LEAD_PRICE,
  planOptions,
  planBillingSuffix,
  planTermNote,
  planIsCancelAnytime,
  whatsappHref,
  PlanOption,
} from '@/data/constants';


/**
 * PLAN PICKER — the destination of the floating "Start Now" CTA.
 *
 * Shows every way to buy access, in one glance:
 *   Single Lead  $29          -> 1 lead credit
 *   Basic        $99/mo       -> 5 credits per cycle
 *   Pro          $199/mo      -> 10 credits per cycle
 *   Premium      $499/mo      -> 20 credits per cycle (12-month commitment)
 *
 * Pricing is NEVER retyped here — every number comes from LEAD_PRICE and
 * planOptions() in src/data/constants.ts, which is also what the Pricing page,
 * the checkout modal and the payment webhook agree on.
 *
 * Choosing an option deep-links to the Pricing page with ?plan=<code>, which
 * opens the real hosted-checkout flow there (including the login redirect for
 * signed-out visitors). Nothing is charged from this modal.
 *
 * A WhatsApp row sits at the bottom so a hesitant visitor has an instant
 * answer path instead of closing the tab.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  /** Where the click came from, recorded on the analytics event. */
  source?: string;
}

const PlansModal: React.FC<Props> = ({ open, onClose, source = 'floating-cta' }) => {
  const { t, lang, path } = useI18n();
  const navigate = useNavigate();

  // Register with the overlay registry: locks page scroll behind the dialog and
  // tells the floating action stack to get out of the way.
  useOverlayLock(open);

  // Escape closes the dialog.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (plan: string) => {
    track('plan_selected', { plan, source });
    onClose();
    navigate(`${path('/pricing')}?plan=${encodeURIComponent(plan)}`);
  };

  const onWhatsApp = () => {
    track('cta_click', { action: 'whatsapp', channel: 'plans-modal', source });
  };

  const plans: PlanOption[] = planOptions(lang);

  return (
    <div
      className="fixed inset-0 z-[130] flex items-end justify-center bg-slate-900/60 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('cta.plansTitle')}
      onClick={onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl sm:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900 sm:text-2xl">{t('cta.plansTitle')}</h2>
            <p className="mt-1 text-sm text-slate-500">{t('cta.plansSub')}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ---- Single lead ($29) ---------------------------------------- */}
        <button
          onClick={() => go('single')}
          className="mt-5 flex w-full items-center justify-between gap-4 rounded-2xl border-2 border-slate-900 bg-slate-900 px-5 py-4 text-left text-white transition-colors hover:bg-slate-800"
        >
          <span className="flex items-start gap-3">
            <Zap className="mt-0.5 h-5 w-5 shrink-0 text-orange-400" />
            <span>
              <span className="block text-base font-extrabold">{t('cta.singleLead')}</span>
              <span className="mt-0.5 block text-sm text-slate-300">{t('cta.singleLeadDesc')}</span>
            </span>
          </span>
          <span className="shrink-0 text-right">
            <span className="block text-2xl font-extrabold">{money(LEAD_PRICE)}</span>
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {t('cta.oneCredit')}
            </span>
          </span>
        </button>

        {/* ---- Membership plans ----------------------------------------- */}
        <p className="mt-6 text-xs font-bold uppercase tracking-wide text-slate-400">
          {t('cta.plansHeading')}
        </p>
        <div className="mt-3 grid gap-4 sm:grid-cols-3">
          {plans.map((p, i) => (
            <div
              key={p.code}
              className={`flex flex-col rounded-2xl border p-4 ${
                i === 1
                  ? 'border-orange-300 bg-orange-50/50 ring-1 ring-orange-200'
                  : 'border-slate-200 bg-white'
              }`}
            >
              <h3 className="text-base font-extrabold text-slate-900">{p.name}</h3>
              <p className="mt-1">
                <span className="text-2xl font-extrabold text-slate-900">{money(p.price)}</span>
                <span className="text-sm font-semibold text-slate-400">
                  {planBillingSuffix(p.code, lang)}
                </span>
              </p>
              <p className="mt-2 flex items-start gap-1.5 text-sm font-semibold text-slate-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                {t('cta.creditsPerCycle', { n: p.leads })}
              </p>
              <p className="mt-1.5 flex-1 text-xs leading-relaxed text-slate-500">
                {/*
                  "Cancel anytime" is gated on planIsCancelAnytime() so a
                  committed plan (Premium, 12 months) can never contradict its
                  own agreement. Premium shows the approved disclosure instead.
                */}
                {planIsCancelAnytime(p.code) ? t('cta.cancelAnytime') : planTermNote(p.code, p.price, lang)}
              </p>

              <button
                onClick={() => go(p.code)}
                className={`mt-4 w-full rounded-xl px-4 py-3 text-sm font-bold transition-colors ${
                  i === 1
                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                {t('pricing.choosePlan')}
              </button>
            </div>
          ))}
        </div>

        <p className="mt-5 flex items-start gap-2 rounded-xl bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-500">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          {t('cta.creditRules')}
        </p>

        <button
          onClick={() => go('compare')}
          className="mt-4 flex w-full items-center justify-center gap-1.5 text-sm font-bold text-orange-600 hover:underline"
        >
          {t('cta.compareAll')} <ArrowRight className="h-4 w-4" />
        </button>

        {/* ---- Still hesitating? Talk to a human. ----------------------- */}
        <div className="mt-5 flex flex-col items-center gap-2 border-t border-slate-100 pt-5 sm:flex-row sm:justify-between">
          <div className="text-center sm:text-left">
            <p className="text-sm font-bold text-slate-900">{t('cta.questions')}</p>
            <p className="text-xs text-slate-500">{t('cta.replyFast')}</p>
          </div>
          <a
            href={whatsappHref(lang)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onWhatsApp}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-[#1eb356] sm:w-auto"
          >
            <MessageCircle className="h-4 w-4" />
            {t('cta.chatNow')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default PlansModal;
